import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Delete, LogIn, LogOut, Clock, Users, Lock, Unlock, MapPin, Camera, Maximize, AlertTriangle, Megaphone } from "lucide-react";
import { KioskTipPrompt } from "@/components/TipReport";

const KEY = "h-16 rounded-xl bg-white/5 hover:bg-white/10 active:scale-95 transition-all text-2xl font-mono-num font-semibold";

function useCamera(enabled) {
  const videoRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user", width: 320, height: 240 }, audio: false })
      .then((s) => { stream = s; if (videoRef.current) { videoRef.current.srcObject = s; setReady(true); } })
      .catch(() => setDenied(true));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [enabled]);
  const capture = () => {
    const v = videoRef.current;
    if (!ready || !v || !v.videoWidth) return null;
    const c = document.createElement("canvas");
    c.width = 240; c.height = Math.round((v.videoHeight / v.videoWidth) * 240) || 180;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.6);
  };
  return { videoRef, ready, denied, capture };
}

function getPosition() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => resolve(null), { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
  });
}

export default function TimeClock() {
  const { companyId: lockedCompany } = useParams();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(() => lockedCompany || localStorage.getItem("kioskCompany") || "");
  const [config, setConfig] = useState(null);
  const [pin, setPin] = useState("");
  const [now, setNow] = useState(new Date());
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [roster, setRoster] = useState([]);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [unlockErr, setUnlockErr] = useState("");
  const resetRef = useRef(null);
  const cam = useCamera(!!config?.photo_enabled);

  useEffect(() => {
    api.get("/timeclock/companies").then((r) => {
      setCompanies(r.data);
      if (lockedCompany) setCompanyId(lockedCompany);
      else if (!companyId && r.data[0]) setCompanyId(r.data[0].id);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadRoster = useCallback(() => {
    if (!companyId) return;
    api.get(`/timeclock/roster/${companyId}`).then((r) => setRoster(r.data)).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    localStorage.setItem("kioskCompany", companyId);
    loadRoster();
    api.get(`/timeclock/config/${companyId}`).then((r) => setConfig(r.data)).catch(() => setConfig(null));
  }, [companyId, loadRoster]);

  const locked = !!lockedCompany && !!config?.has_passcode;

  // In locked mode, keep the browser from being navigated away casually
  useEffect(() => {
    if (!locked) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [locked]);

  const press = (d) => { setError(""); if (pin.length < 6) setPin((p) => p + d); };

  const KQ = "kioskOfflineQueue";
  const readKQ = () => { try { return JSON.parse(localStorage.getItem(KQ) || "[]"); } catch { return []; } };
  const [kq, setKq] = useState(readKQ());
  const [syncIssues, setSyncIssues] = useState([]);
  const syncQueue = async () => {
    let q = readKQ(); if (!q.length || !navigator.onLine) return;
    let n = 0;
    for (const p of [...q]) {
      try { await api.post("/timeclock/punch", p); q = q.filter((x) => x.client_id !== p.client_id); n++; }
      catch (e) {
        if (e.response?.status && e.response.status < 500) {
          q = q.filter((x) => x.client_id !== p.client_id);
          const msg = formatApiErrorDetail(e.response?.data?.detail);
          setSyncIssues((s) => [{ at: p.at, msg }, ...s].slice(0, 5));
          api.post("/timeclock/sync-failure", { company_id: p.company_id, at: p.at, pin_last2: String(p.pin || "").slice(-2), reason: msg }).catch(() => {});
        }
        else break;
      }
      localStorage.setItem(KQ, JSON.stringify(q));
    }
    setKq(q); if (n) loadRoster();
  };
  useEffect(() => { const up = () => syncQueue(); window.addEventListener("online", up); const t = setInterval(syncQueue, 30000); syncQueue(); return () => { window.removeEventListener("online", up); clearInterval(t); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (pin.length < 4 || !companyId) { setError("Enter your 4-digit PIN"); return; }
    setBusy(true);
    const at = new Date().toISOString(); const client_id = `k${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    try {
      const payload = { company_id: companyId, pin, at, client_id };
      if (config?.geofence_enabled) Object.assign(payload, (await getPosition()) || {});
      if (config?.photo_enabled) { const photo = cam.capture(); if (photo) payload.photo = photo; }
      if (!navigator.onLine) throw Object.assign(new Error("offline"), { offline: true });
      const { data } = await api.post("/timeclock/punch", payload);
      setResult({ ...data, photo: payload.photo, pin: data.action === "out" && data.tipped ? pin : undefined });
      setPin(""); setError("");
      loadRoster();
      clearTimeout(resetRef.current);
      resetRef.current = setTimeout(() => setResult(null), data.action === "out" && data.tipped ? 25000 : data.announcements?.items?.length ? 15000 : 5000);
    } catch (e) {
      if (e.offline || !e.response || e.response.status >= 500 || typeof e.response.data !== "object") {
        const { photo, ...rest } = { company_id: companyId, pin, at, client_id };
        const q = [...readKQ(), rest]; localStorage.setItem(KQ, JSON.stringify(q)); setKq(q);
        setResult({ action: "offline", employee_name: "Saved offline", job_title: `PIN ending ${pin.slice(-2)} · ${new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`, time: at });
        setPin(""); setError("");
        clearTimeout(resetRef.current); resetRef.current = setTimeout(() => setResult(null), 5000);
      } else { setError(formatApiErrorDetail(e.response?.data?.detail)); setPin(""); }
    } finally { setBusy(false); }
  };

  const tryUnlock = async () => {
    try {
      await api.post("/timeclock/verify-passcode", { company_id: companyId, passcode });
      setUnlockOpen(false); setPasscode(""); setUnlockErr("");
      if (document.fullscreenElement) document.exitFullscreen?.();
      navigate("/timeclock");
    } catch (e) {
      setUnlockErr(formatApiErrorDetail(e.response?.data?.detail)); setPasscode("");
    }
  };

  const goFullscreen = () => document.documentElement.requestFullscreen?.().catch(() => {});

  const company = config?.company_id === companyId ? config : companies.find((c) => c.id === companyId);
  const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const dateStr = now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const accent = company?.color || "#2563EB";
  const [msgIdx, setMsgIdx] = useState(0);
  useEffect(() => { const n = (config?.messages || []).length; if (n < 2) return; const t = setInterval(() => setMsgIdx((i) => (i + 1) % n), 8000); return () => clearInterval(t); }, [config?.messages]);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="timeclock-kiosk">
      <header className="flex items-center justify-between gap-3 px-6 sm:px-10 py-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-lg px-2 py-1.5">
            <img src={company?.logo_url || "/rightpay-logo.png"} alt={company?.name || "RightPay"} className="h-9 w-auto max-w-[160px] object-contain" data-testid="kiosk-header-logo" />
          </div>
          {company?.logo_url && <span className="text-[10px] text-slate-500 hidden sm:block">Powered by RightPay</span>}
        </div>
        <div className="flex items-center gap-2">
          {config?.geofence_enabled && <span data-testid="kiosk-geo-badge" className="hidden sm:flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300"><MapPin className="w-3 h-3" /> Location</span>}
          {config?.photo_enabled && <span data-testid="kiosk-photo-badge" className="hidden sm:flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-white/5 border border-white/10 text-slate-300"><Camera className="w-3 h-3" /> Photo</span>}
          {lockedCompany ? (
            <div data-testid="kiosk-locked-company" className="flex items-center gap-2 font-semibold px-3 py-2 rounded-lg bg-white/5 border border-white/15">
              {locked ? <Lock className="w-4 h-4 text-amber-400" /> : <span>{company?.logo}</span>} {company?.name || "Location"}
            </div>
          ) : (
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger data-testid="kiosk-company-select" className="w-56 bg-white/5 border-white/15 text-white">
                <SelectValue placeholder="Select location…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`kiosk-company-${c.id}`}>
                    <span className="mr-1.5">{c.logo}</span> {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {lockedCompany && (
            <>
              <button data-testid="kiosk-fullscreen-btn" onClick={goFullscreen} title="Fullscreen" className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10"><Maximize className="w-4 h-4" /></button>
              <button data-testid="kiosk-exit-btn" onClick={() => (locked ? setUnlockOpen(true) : navigate("/timeclock"))} title="Exit kiosk"
                className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10">{locked ? <Unlock className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}</button>
            </>
          )}
        </div>
      </header>

      <div className="flex-1 grid lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col items-center justify-center p-6 sm:p-10">
          <div className="text-center mb-6">
            {company?.logo_url && (
              <div className="mx-auto mb-5 inline-flex items-center justify-center rounded-2xl bg-white/95 px-8 py-5 shadow-lg" style={{ boxShadow: `0 0 0 4px ${accent}, 0 20px 50px -20px ${accent}` }} data-testid="kiosk-idle-logo-wrap">
                <img src={company.logo_url} alt={company.name} className="h-20 sm:h-28 w-auto max-w-[360px] object-contain" data-testid="kiosk-idle-logo" />
              </div>
            )}
            <div className="font-mono-num text-6xl sm:text-7xl font-bold tracking-tight" data-testid="kiosk-clock">{timeStr}</div>
            <div className="text-slate-400 mt-2">{dateStr}</div>
            {company && <div className="mt-3 font-semibold text-lg inline-flex items-center gap-2" style={{ color: accent }}>{!company.logo_url && company.logo} {company.name}<span className="h-1 w-10 rounded-full" style={{ background: accent }} /></div>}
            {(config?.messages || []).length > 0 && (
              <div key={msgIdx} data-testid="kiosk-welcome-message" className="mt-4 mx-auto max-w-xl rounded-xl border px-5 py-3 text-base sm:text-lg text-slate-100 animate-fade-up" style={{ borderColor: `${accent}66`, background: `${accent}1f` }}>
                <span className="mr-2 inline-block w-2 h-2 rounded-full align-middle" style={{ background: accent }} />{config.messages[msgIdx % config.messages.length]}
              </div>
            )}
            {(kq.length > 0 || syncIssues.length > 0) && (
              <div data-testid="kiosk-offline-banner" className="mt-3 inline-flex flex-col items-center gap-1 rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-300">
                {kq.length > 0 && <span>{kq.length} punch{kq.length === 1 ? "" : "es"} saved offline — syncing when connection returns</span>}
                {syncIssues.map((s, i) => <span key={i} className="text-rose-300">Not synced ({new Date(s.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}): {s.msg}</span>)}
              </div>
            )}
          </div>

          {result ? (
            <div data-testid="kiosk-result" className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-6 text-center animate-fade-up">
              <div className="flex items-center justify-center gap-4 mb-4">
                {result.photo && <img data-testid="kiosk-result-photo" src={result.photo} alt="you" className="w-16 h-16 rounded-2xl object-cover border border-white/20" />}
                <div className={`grid place-items-center w-16 h-16 rounded-2xl ${result.action === "in" ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                  {result.action === "in" ? <LogIn className="w-8 h-8" /> : <LogOut className="w-8 h-8" />}
                </div>
              </div>
              <div className="text-2xl font-bold font-display">{result.employee_name}</div>
              <div className="text-slate-400 text-sm">{result.job_title}</div>
              <div className={`mt-3 text-lg font-semibold ${result.action === "in" ? "text-emerald-400" : "text-amber-400"}`}>
                {result.action === "offline" ? "Saved offline — will sync when back online" : `Clocked ${result.action === "in" ? "In" : "Out"}`} · {new Date(result.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              {result.hours != null && <div className="text-slate-400 text-sm mt-1 font-mono-num">Shift: {result.hours.toFixed(2)} hrs</div>}
              {result.action === "in" && result.shifts && (result.shifts.today || result.shifts.next) && (
                <div data-testid="kiosk-shifts" className="mt-3 grid grid-cols-2 gap-2 text-left">
                  <div className="rounded-xl bg-white/5 border border-white/10 p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-400">Today ends</div>{result.shifts.today ? <><div className="text-lg font-bold font-mono-num text-white" data-testid="kiosk-today-end">{new Date(result.shifts.today.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div><div className="text-[11px] text-slate-400 truncate">{result.shifts.today.role}{result.shifts.today.location ? ` · ${result.shifts.today.location}` : ""}</div></> : <div className="text-sm text-slate-400">No shift scheduled</div>}</div>
                  <div className="rounded-xl bg-white/5 border border-white/10 p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-400">Next shift</div>{result.shifts.next ? <><div className="text-sm font-bold text-white" data-testid="kiosk-next-shift">{new Date(result.shifts.next.date + "T12:00:00").toLocaleDateString([], { weekday: "short", month: "numeric", day: "numeric" })} · {new Date(result.shifts.next.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–{new Date(result.shifts.next.end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div><div className="text-[11px] text-slate-400 truncate">{result.shifts.next.role}{result.shifts.next.location ? ` · ${result.shifts.next.location}` : ""}</div></> : <div className="text-sm text-slate-400">Nothing scheduled yet</div>}</div>
                </div>
              )}
              {result.action === "in" && result.announcements?.items?.length > 0 && (
                <div data-testid="kiosk-announcements" className="mt-4 text-left rounded-xl border border-blue-400/30 bg-blue-500/10 p-3 space-y-2">
                  <div className="text-xs font-semibold text-blue-300 flex items-center gap-1.5"><Megaphone className="w-3.5 h-3.5" /> {result.announcements.unread} new message{result.announcements.unread === 1 ? "" : "s"} from your employer</div>
                  {result.announcements.items.map((a) => <div key={a.id} data-testid={`kiosk-ann-${a.id}`}><div className="text-sm font-semibold text-white">{a.pinned ? "📌 " : ""}{a.title}</div><div className="text-xs text-slate-300 whitespace-pre-wrap line-clamp-3">{a.body}</div>{a.attachments > 0 && <div className="text-[11px] text-slate-400">{a.attachments} attachment{a.attachments === 1 ? "" : "s"} — open Messages in the RightPay app</div>}</div>)}
                  {result.announcements.unread > result.announcements.items.length && <div className="text-[11px] text-slate-400">+{result.announcements.unread - result.announcements.items.length} more in the RightPay app</div>}
                </div>
              )}
              {result.location && (
                <div data-testid="kiosk-result-location" className={`mt-2 text-xs flex items-center justify-center gap-1 ${result.location.on_site === false ? "text-rose-400" : result.location.on_site ? "text-emerald-400" : "text-slate-400"}`}>
                  {result.location.on_site === false ? <AlertTriangle className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}
                  {result.location.unavailable ? "Location unavailable — punch logged" : result.location.on_site === false ? `Off-site (${result.location.distance_m}m) — flagged for HR` : result.location.on_site ? `On-site · ${result.location.distance_m}m` : "Location recorded"}
                </div>
              )}
              {result.action === "out" && result.tipped && (
                <KioskTipPrompt companyId={companyId} pin={result.pin} onDone={() => { clearTimeout(resetRef.current); resetRef.current = setTimeout(() => setResult(null), 4000); }} />
              )}
            </div>
          ) : (
            <div className="w-full max-w-xs">
              {config?.photo_enabled && (
                <div className="mb-4 rounded-xl overflow-hidden border border-white/10 bg-black aspect-[4/3] relative">
                  <video ref={cam.videoRef} autoPlay muted playsInline className="w-full h-full object-cover -scale-x-100" data-testid="kiosk-camera" />
                  {cam.denied && <div className="absolute inset-0 grid place-items-center text-xs text-amber-400 bg-black/70 px-4 text-center"><span><Camera className="w-5 h-5 mx-auto mb-1" />Camera unavailable — punches will be recorded without a photo</span></div>}
                </div>
              )}
              <div className="flex justify-center gap-3 mb-6" data-testid="kiosk-pin-display">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <span key={i} className="w-4 h-4 rounded-full transition-colors" style={{ background: i < pin.length ? accent : "rgba(255,255,255,0.15)" }} />
                ))}
              </div>
              {error && <p data-testid="kiosk-error" className="text-center text-rose-400 text-sm mb-4">{error}</p>}
              <div className="grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                  <button key={n} data-testid={`kiosk-key-${n}`} onClick={() => press(String(n))} className={KEY}>{n}</button>
                ))}
                <button data-testid="kiosk-clear" onClick={() => setPin("")} className={`${KEY} !text-sm font-medium text-slate-400`}>Clear</button>
                <button data-testid="kiosk-key-0" onClick={() => press("0")} className={KEY}>0</button>
                <button data-testid="kiosk-backspace" onClick={() => setPin((p) => p.slice(0, -1))} className={`${KEY} grid place-items-center`}><Delete className="w-6 h-6" /></button>
              </div>
              <button data-testid="kiosk-punch-btn" onClick={submit} disabled={busy} style={{ background: accent }}
                className="w-full mt-4 h-14 rounded-xl hover:opacity-90 active:scale-[0.99] transition-all text-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                <Clock className="w-5 h-5" /> {busy ? "Recording…" : "Clock In / Out"}
              </button>
            </div>
          )}
        </div>

        <aside className="border-t lg:border-t-0 lg:border-l border-white/10 p-6 bg-white/[0.02]">
          <div className="flex items-center gap-2 mb-4 text-slate-300">
            <Users className="w-5 h-5" />
            <h2 className="font-display font-semibold">On the Clock ({roster.length})</h2>
          </div>
          <div className="space-y-2" data-testid="kiosk-roster">
            {roster.map((r, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl bg-white/5 p-3">
                <Avatar className="w-9 h-9"><AvatarImage src={r.avatar} /><AvatarFallback>{r.employee_name?.[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{r.employee_name}</div>
                  <div className="text-xs text-slate-400">{r.job_title}</div>
                </div>
                <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-mono-num">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  {new Date(r.since).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
            {roster.length === 0 && <p className="text-slate-500 text-sm">No one is currently clocked in.</p>}
          </div>
        </aside>
      </div>

      {unlockOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 grid place-items-center p-6" data-testid="kiosk-unlock-modal">
          <div className="w-full max-w-xs rounded-2xl bg-slate-900 border border-white/10 p-6 text-center">
            <Lock className="w-8 h-8 mx-auto text-amber-400 mb-3" />
            <div className="font-display font-semibold text-lg">Kiosk is locked</div>
            <p className="text-sm text-slate-400 mt-1">Enter the device passcode to exit this location.</p>
            <input data-testid="kiosk-unlock-input" type="password" inputMode="numeric" autoFocus value={passcode} maxLength={8}
              onChange={(e) => { setPasscode(e.target.value.replace(/\D/g, "")); setUnlockErr(""); }}
              onKeyDown={(e) => e.key === "Enter" && tryUnlock()}
              className="mt-4 w-full h-12 rounded-xl bg-white/5 border border-white/15 text-center text-2xl tracking-[0.5em] font-mono-num outline-none focus:border-primary" />
            {unlockErr && <p data-testid="kiosk-unlock-error" className="text-rose-400 text-sm mt-2">{unlockErr}</p>}
            <div className="grid grid-cols-2 gap-2 mt-4">
              <button data-testid="kiosk-unlock-cancel" onClick={() => { setUnlockOpen(false); setPasscode(""); setUnlockErr(""); }} className="h-11 rounded-xl bg-white/5 hover:bg-white/10">Cancel</button>
              <button data-testid="kiosk-unlock-submit" onClick={tryUnlock} className="h-11 rounded-xl bg-primary hover:opacity-90 font-semibold">Unlock</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
